import { useInitConfirmEmailMutation } from '@generated/graphql';
import { Button, Stack, Typography } from '@mui/material';
import { useToast } from '@myhrbox/myhrbox-frontend-core';
import useResendTimer from 'hooks/useResendTimer';
import AuthSplitShell from 'pages/onboarding/components/AuthSplitShell';
import AuthTabs from 'pages/onboarding/components/AuthTabs';
import RegisterForm from 'pages/Register/RegisterForm';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { RegisterFormData } from './types';

const RESEND_TIMER_SECONDS = 60;

const Register = () => {
  const { t } = useTranslation();
  const { showErrorToast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const form = useForm<RegisterFormData>();
  const [searchParams] = useSearchParams();
  const [initConfirmEmail, { loading: resendLoading }] = useInitConfirmEmailMutation();
  const { resendTimer, isResendTimerActive, startResendTimer } =
    useResendTimer(RESEND_TIMER_SECONDS);
  const redirectUrl = searchParams.get('redirectUrl') || undefined;

  const handleResend = () => {
    initConfirmEmail()
      .then((res) => {
        if (!res.data?.initConfirmEmail) throw new Error('initConfirmEmail failed');
        startResendTimer();
      })
      .catch(() => {
        showErrorToast(t('errors.common'));
      });
  };

  return (
    <AuthSplitShell>
      <Stack spacing={{ xs: 4, lg: 4.5 }}>
        <AuthTabs activeTab='register' redirectUrl={redirectUrl} />

        <Stack spacing={3}>
          {isSubmitted ? (
            <Stack spacing={2}>
              <Typography variant='h28'>{t('register.submittedTitle')}</Typography>
              <Typography component='p' variant='subtitle18rg'>
                {t('register.submittedMessage', { email: form.getValues('email') })}
              </Typography>
              <Button
                variant='text'
                onClick={handleResend}
                sx={{ width: 'fit-content', minWidth: 0, px: 0, fontSize: 16, fontWeight: 600 }}
                disabled={isResendTimerActive || resendLoading}>
                {isResendTimerActive
                  ? t('register.resendEmailWithTimer', { resendTimer })
                  : t('register.resendEmail')}
              </Button>
            </Stack>
          ) : (
            <RegisterForm
              form={form}
              onSuccess={() => {
                setIsSubmitted(true);
                startResendTimer();
              }}
            />
          )}
        </Stack>
      </Stack>
    </AuthSplitShell>
  );
};

export default Register;
